import React, {useEffect, useState} from "react";
import {Platform, StyleSheet, View, Text, Pressable} from "react-native";
import {useRecoilValue} from "recoil";
import {isBackGuardArmed, disarmBackGuard} from "./lib/backGuard";
import {cartState} from "./state/cartState";
import {hasCompletedOrder} from "./lib/lastOrder";
import safeStorage from "./lib/storage";
import {PALETTE} from "./lib/palette";
import apps from "./lib/apps";

// Leave 모달 문구를 상황별로: 완료 주문 > 카트에 담긴 항목 있음 > 빈 카트.
function leaveMessage(cartQty: number): string {
    const uuid = safeStorage.getItem("uuid") || "";
    if (hasCompletedOrder(uuid)) return "If you leave, you won't be able to view your order.";
    if (cartQty > 0) return "If you leave, all items in your cart will be removed.";
    return "If you leave, you'll need to scan the table QR code again to come back.";
}

// 실기 검증용 디버그 배지(히스토리 길이/무장/모달). 배포 전 false로.
const DEBUG_BADGE = false;

/**
 * App 레벨 상주 back 가드.
 * QR 주문 세션 중(=armBackGuard 호출 후) 브라우저/기기 back을 popstate로 잡아
 * 현재 화면(메뉴 등) 위에 Leave/Stay 모달을 띄운다. Splash는 AppStack 전환 시 언마운트되므로
 * 이 컴포넌트는 반드시 App.tsx 레벨(상주)에 있어야 리스너가 살아남는다.
 *
 * 히스토리 흐름: entry0=랜딩, [Tap to Start 제스처가 pushState → entry1(메뉴)]. back → popstate(entry0)
 * → 모달. Stay(제스처)=pushState 재무장(entry1). Leave=history.back()(entry0→탭 이탈).
 */
export default function BackGuard() {
    const [visible, setVisible] = useState(false);
    const [, force] = useState(0);                  // 디버그 배지 갱신용
    const cart = useRecoilValue(cartState);         // Leave 문구용 카트 수량

    useEffect(() => {
        if (Platform.OS !== "web") return;
        const onPop = () => {
            if (DEBUG_BADGE) force((n) => n + 1);
            if (!isBackGuardArmed()) return;   // 세션 시작 전/비무장 → 무시(탭 이탈)
            // gotcha#3: 여기서 pushState 재무장 금지(user activation 없음 → skippable). 모달만 띄운다.
            setVisible(true);
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    const accent = apps.getAccent();
    const onAccent = apps.onAccent();

    function stay() {
        // gotcha#3: 재무장은 사용자 제스처(Stay)에서만. 동기 pushState → non-skippable 유지.
        if (typeof window !== "undefined") window.history.pushState({psGuard: 1}, "");
        setVisible(false);
        if (DEBUG_BADGE) force((n) => n + 1);
    }

    function leave() {
        disarmBackGuard();
        setVisible(false);
        // 삼성 카메라-탭은 탭을 강제로 닫을 수 없고 history.back()도 no-op이다. 대신 QR 파라미터를 제거한
        // URL로 replace 이동(엔트리 추가 없이 교체) → ①새로고침해도 재진입 불가(반드시 QR 재스캔, 악용 차단)
        // ②Leave가 히스토리를 안 늘리고 뒤 엔트리도 expired로 수렴 ③clean 페이지에서 back = 탭 이탈.
        // shopId가 있으면 `?shopId/_bye`로 남긴다 → 여전히 만료(파라미터 부족)지만 Splash가 매장 로고를
        // 로드하고 `_bye` 마커로 "See you next time" 작별 화면을 띄운다(Leave 때만).
        if (typeof window !== "undefined") {
            const base = window.location.origin + window.location.pathname;
            const sid = (apps.shopId || "").trim();
            window.location.replace(sid ? base + "?" + sid + "/_bye" : base);
        }
    }

    return (
        <>
            {DEBUG_BADGE ? (
                <Text style={styles.badge}>
                    {`hist=${typeof window !== "undefined" ? window.history.length : "-"} armed=${isBackGuardArmed() ? "Y" : "N"} m=${visible ? "1" : "0"}`}
                </Text>
            ) : null}

            {visible ? (
                <View style={styles.overlay} pointerEvents="auto">
                    <View style={styles.card}>
                        <Text style={styles.title}>Are you leaving?</Text>
                        <Text style={styles.desc}>{leaveMessage(cart ? cart.totalQty() : 0)}</Text>
                        <Pressable style={[styles.btn, {backgroundColor: accent}]} onPress={stay}>
                            <Text style={[styles.btnText, {color: onAccent}]}>Stay</Text>
                        </Pressable>
                        <Pressable style={[styles.btn, styles.btnOutline, {borderColor: accent}]} onPress={leave}>
                            <Text style={[styles.btnText, {color: accent}]}>Leave</Text>
                        </Pressable>
                    </View>
                </View>
            ) : null}
        </>
    );
}

const styles = StyleSheet.create({
    badge: {
        position: "absolute",
        top: 6,
        left: 6,
        zIndex: 10000,
        fontSize: 11,
        fontWeight: "700",
        color: "#ffffff",
        backgroundColor: "rgba(0,0,0,0.6)",
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    overlay: {
        position: "absolute",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        backgroundColor: "rgba(0,0,0,0.45)",
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 28,
    },
    card: {
        width: "100%",
        maxWidth: 360,
        backgroundColor: PALETTE.SURFACE,
        borderRadius: 16,
        paddingVertical: 24,
        paddingHorizontal: 22,
        alignItems: "center",
    },
    title: {
        fontSize: 19,
        fontWeight: "800",
        color: PALETTE.TEXT,
        textAlign: "center",
    },
    desc: {
        fontSize: 14,
        color: PALETTE.MUTED,
        lineHeight: 20,
        textAlign: "center",
        marginTop: 8,
        marginBottom: 18,
    },
    btn: {
        width: "100%",
        borderRadius: 12,
        paddingVertical: 14,
        alignItems: "center",
        marginTop: 10,
    },
    btnOutline: {
        backgroundColor: "transparent",
        borderWidth: 1.5,
    },
    btnText: {
        fontSize: 15,
        fontWeight: "800",
    },
});
